import { isEmpty, isNil } from 'ramda';
import messages from './messages';
import {
  ORGANIZATION_FIELDS_MAP,
  INCOME_ROWS_FOR_OHIO_EMPLOYMENT_TYPE_FIELD,
} from './constants';
import {
  getIncomeAlternateFormRulesByState,
  hasStateSpecificIncomeFormLayout,
  OHIO_OTHER_INCOME_TYPE_UNSET,
} from './stateRules';

import { hasDuplicates } from '../validate';
import generalMessages from '../../App/messages';

const checkEmptyNil = (value: string) => {
  return isEmpty(value) || isNil(value);
};

/** Treats 0 as present; only empty / nullish counts as missing (avoids JS falsy bugs). */
const isUnsetNumeric = (value: mixed) =>
  value === '' ||
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.trim() === '');

/**
 * Validates the Income Verification Alternate Form by checking if the required fields are filled out.
 * -
 * @param {*} values a json with the form values
 * @param {*} param1 an object with the props comming from the IncomeVerificationForm component
 * @returns an object with or without errrors
 */
export const validate = (
  values: Object,
  { intl, selectedProperty, programName, flags, isHotmaActive }: Object,
) => {
  const {
    INCOME_VALUE_FIELDS,
    INCOME_ROW_NAMES,
    PAYRATE_VALUE_FIELDS,
    HOURSWEEKSMONTHS_VALUE_FIELDS,
  } = ORGANIZATION_FIELDS_MAP.alternate;
  const mustSelectOneOptionWarning = intl.formatMessage(
    messages.mustSelectOneOption,
  );

  const errors = {};
  const stateCode = selectedProperty?.physicalState;
  const stateRules = hasStateSpecificIncomeFormLayout(
    stateCode,
    programName,
    flags,
    isHotmaActive ?? false,
  )
    ? getIncomeAlternateFormRulesByState(stateCode)
    : null;
  const excludedRows = stateRules?.excludedIncomeVerificationRows || [];

  const employmentList = values?.employmentList ?? [];
  if (checkEmptyNil(values['employment'])) {
    errors['employment'] = mustSelectOneOptionWarning;
  } else if (values['employment'] === 'yes' && employmentList.length < 1) {
    errors['employment'] = 'Employment Rows Cannot Be Empty';
  }

  const employmentListErrorArray = [];
  employmentList.forEach((employmentRow, index) => {
    const employmentRowErrors = {};
    const payRate = employmentRow?.PayRate ?? -1;
    const payFrequency = employmentRow?.PayFrequency ?? -1;
    const hoursWeeksMonths = employmentRow?.HoursWeeksMonths;
    const payType = +(employmentRow?.payType ?? -1);

    const validations = {
      payType: [() => payType === -1 && 'Employment Type Required'],
      PayFrequency: [() => payFrequency === -1 && 'Pay Frequency Required'],
      PayRate: [
        () => (payRate === '' || payRate === undefined) && 'Pay Rate Required',
        () => payRate < 0 && 'Must Be Positive',
      ],
      HoursWeeksMonths: [
        () =>
          (hoursWeeksMonths === '' || hoursWeeksMonths === undefined) &&
          'Hours/Weeks/Months Required',
        () => hoursWeeksMonths < 0 && 'Hours/Weeks/Months Must Be Positive',
        () =>
          payFrequency === 'monthly' &&
          hoursWeeksMonths > 12.0 &&
          intl.formatMessage(messages.valueMustBeBetween, {
            value: 'Months',
            lower: '1',
            upper: '12',
          }),
      ],
    };

    Object.entries(validations).forEach(([key, validationArray]) => {
      validationArray.some((validation) => {
        const result = validation();
        if (result) {
          employmentRowErrors[key] = result;
        }
        return result;
      });
    });

    employmentListErrorArray[index] = employmentRowErrors;
  });

  errors.employmentList = employmentListErrorArray;

  const otherList = values?.otherList ?? [];
  if (checkEmptyNil(values['other'])) {
    errors['other'] = mustSelectOneOptionWarning;
  } else if (values['other'] === 'yes' && otherList.length < 1) {
    errors['other'] = 'Other Income Rows Cannot Be Empty';
  }

  const otherListErrorArray = [];
  otherList.forEach((otherRow, index) => {
    const otherRowErrors = {};
    const payRate = otherRow?.PayRate;
    const payFrequency = otherRow?.PayFrequency ?? -1;
    const hoursWeeksMonths = otherRow?.HoursWeeksMonths;
    const incomeName = otherRow?.IncomeName;
    const incomeType = otherRow?.IncomeType;
    const employmentType = otherRow?.EmploymentType;
    const incomeVerification = otherRow?.IncomeVerification;

    const validations = {
      PayFrequency: [() => payFrequency === -1 && 'Pay Frequency Required'],
      PayRate: [
        () => (payRate === '' || payRate === undefined) && 'Pay Rate Required',
        () => payRate < 0 && 'Must Be Positive',
      ],
      HoursWeeksMonths: [
        () =>
          (hoursWeeksMonths === '' || hoursWeeksMonths === undefined) &&
          'Hours/Weeks/Months Required',
        () => hoursWeeksMonths < 0 && 'Hours/Weeks/Months Must Be Positive',
        () =>
          payFrequency === 'monthly' &&
          hoursWeeksMonths > 12.0 &&
          'Months Must Be Between 1 and 12',
      ],
      IncomeName: [
        () => !incomeName && 'Income Name Required',
        () =>
          hasDuplicates(otherList, incomeName, 'IncomeName') &&
          intl.formatMessage(generalMessages.duplicateIdentifiers, {
            item: '"Other" rows',
            identifiers: 'names',
          }),
      ],
      IncomeType: [
        () => {
          const missing = stateRules
            ? +incomeType === OHIO_OTHER_INCOME_TYPE_UNSET ||
              checkEmptyNil(incomeType)
            : checkEmptyNil(incomeType);
          return missing && 'Income Type Required';
        },
      ],
      EmploymentType: [
        () =>
          stateRules &&
          values.other === 'yes' &&
          (+employmentType === -1 || checkEmptyNil(employmentType)) &&
          'Employment Type Required',
      ],
      IncomeVerification: [
        () =>
          stateRules?.requiresIncomeVerification &&
          values.other === 'yes' &&
          (+incomeVerification === -1 || checkEmptyNil(incomeVerification)) &&
          'Income Verification Required',
      ],
    };

    Object.entries(validations).forEach(([key, validationArray]) => {
      validationArray.some((validation) => {
        const result = validation();
        if (result) {
          otherRowErrors[key] = result;
        }
        return result;
      });
    });

    otherListErrorArray[index] = otherRowErrors;
  });
  errors.otherList = otherListErrorArray;

  INCOME_ROW_NAMES.forEach((row) => {
    const isCurrentIncomeRowInTheFieldsList = INCOME_VALUE_FIELDS.includes(
      `${row}Income`,
    );

    if (checkEmptyNil(values[row]) && isCurrentIncomeRowInTheFieldsList) {
      errors[`${row}Income`] = mustSelectOneOptionWarning;
    } else if (
      values[row] === 'yes' &&
      isCurrentIncomeRowInTheFieldsList &&
      isUnsetNumeric(values[`${row}Income`])
    ) {
      errors[`${row}Income`] = intl.formatMessage(messages.amountRequired);
    }
    if (values[`${row}Income`] < 0) {
      errors[`${row}Income`] = intl.formatMessage(messages.mustBePositive);
    }

    if (
      stateRules?.requiresIncomeVerification &&
      row !== 'other' &&
      !excludedRows.includes(row) &&
      values[row] === 'yes' &&
      (+values[`${row}IncomeVerification`] === -1 ||
        checkEmptyNil(values[`${row}IncomeVerification`]))
    ) {
      errors[`${row}IncomeVerification`] = 'Income Verification Required';
    }
  });

  // Validate payDataColumns
  INCOME_ROW_NAMES.forEach((row) => {
    const isCurrentPayRateRowInTheFieldsList = PAYRATE_VALUE_FIELDS.includes(
      `${row}PayRate`,
    );
    if (checkEmptyNil(values[row]) && isCurrentPayRateRowInTheFieldsList) {
      errors[`${row}PayRate`] = mustSelectOneOptionWarning;
    } else if (
      values[row] === 'yes' &&
      isCurrentPayRateRowInTheFieldsList &&
      isUnsetNumeric(values[`${row}PayRate`])
    ) {
      errors[`${row}PayRate`] = intl.formatMessage(messages.amountRequired);
    }
    if (values[`${row}PayRate`] < 0) {
      errors[`${row}PayRate`] = intl.formatMessage(messages.mustBePositive);
    }

    if (
      values[row] === 'yes' &&
      (!values[`${row}PayFrequency`] || +values[`${row}PayFrequency`] === -1)
    ) {
      errors[`${row}PayFrequency`] = intl.formatMessage(
        messages.frequencyRequired,
      );
    }

    const hoursWeeksMonthsRow = HOURSWEEKSMONTHS_VALUE_FIELDS.includes(
      `${row}HoursWeeksMonths`,
    );
    if (checkEmptyNil(values[row]) && hoursWeeksMonthsRow) {
      errors[`${row}HoursWeeksMonths`] = mustSelectOneOptionWarning;
    } else if (
      values[row] === 'yes' &&
      hoursWeeksMonthsRow &&
      isUnsetNumeric(values[`${row}HoursWeeksMonths`])
    ) {
      errors[`${row}HoursWeeksMonths`] = intl.formatMessage(
        messages.amountRequired,
      );
    }
    if (values[`${row}HoursWeeksMonths`] < 0) {
      errors[`${row}HoursWeeksMonths`] = intl.formatMessage(
        messages.mustBePositive,
      );
    }
    if (
      values[`${row}HoursWeeksMonths`] &&
      values[`${row}PayFrequency`] === 'monthly' &&
      values[`${row}HoursWeeksMonths`] > 12
    ) {
      errors[`${row}HoursWeeksMonths`] = intl.formatMessage(
        messages.valueMustBeBetween,
        {
          value: 'Months',
          lower: '1',
          upper: '12',
        },
      );
    }
  });

  if (stateRules) {
    INCOME_ROWS_FOR_OHIO_EMPLOYMENT_TYPE_FIELD.forEach((row) => {
      if (values[row] !== 'yes') {
        return;
      }
      const et = values[`${row}EmploymentType`];
      if (+et === -1 || checkEmptyNil(et)) {
        errors[`${row}EmploymentType`] = 'Employment Type Required';
      }
    });
  }

  if (checkEmptyNil(values.notWorking)) {
    errors.notWorking = mustSelectOneOptionWarning;
  }

  if (checkEmptyNil(values.collectChildSupport)) {
    errors.collectChildSupport = mustSelectOneOptionWarning;
  } else if (
    values.collectChildSupport === 'yes' &&
    checkEmptyNil(values.collectChildSupportCounty)
  ) {
    errors.collectChildSupportCounty = intl.formatMessage(
      messages.stateAndCountyRequired,
    );
  }

  if (checkEmptyNil(values.anticipateChildSupport)) {
    errors.anticipateChildSupport = mustSelectOneOptionWarning;
  } else if (
    values.anticipateChildSupport === 'yes' &&
    checkEmptyNil(values.anticipateChildSupportCounty)
  ) {
    errors.anticipateChildSupportCounty = intl.formatMessage(
      messages.stateAndCountyRequired,
    );
  }

  if (checkEmptyNil(values.publicHousing)) {
    errors.publicHousing = mustSelectOneOptionWarning;
  } else if (
    values.publicHousing === 'yes' &&
    checkEmptyNil(values.publicHousingText?.trim())
  ) {
    errors.publicHousingText = intl.formatMessage(
      messages.publicHousingAuthorityRequired,
    );
  }

  return errors;
};

export default validate;
